// PatternBackground.js v3.2 （タイル最大サイズ制限付き）
export default class PatternBackground {
  /**
   * パターン背景クラスのコンストラクタ
   * @param {Object} options
   * @param {HTMLCanvasElement} [options.canvas=document.getElementById("pattern")] - 描画先のキャンバス要素
   * @param {number} [options.minGroup=16] - 最小グループサイズ（px）
   * @param {number} [options.interval=10000] - パターン更新間隔（ms）
   * @param {Array<Array<string>>} [options.colorSets=PatternBackground.defaultColors] - カラーセット配列
   * @param {number} [options.fadeDuration=3000] - フェードアニメーション時間（ms）
   * @param {number} [options.resizeDelay=300] - リサイズ時デバウンス時間（ms）
   * @param {Array<string|HTMLImageElement>} [options.images=[]] - タイルに使用する画像リスト
   * @param {number} [options.imageChance=0.1] - タイルを画像に置き換える確率（0〜1）
   * @param {number} [options.maxTilePx=256] - タイルの最大ピクセル幅
   */
  constructor({
    canvas = document.getElementById("pattern"),
    minGroup = 16,
    interval = 10000,
    colorSets = PatternBackground.defaultColors,
    fadeDuration = 3000,
    resizeDelay = 300,
    images = [],
    imageChance = 0.1,
    maxTilePx = 256,
  } = {}) {
    if (!canvas) throw new Error("canvas 要素が必要です");

    this.canvas = canvas;
    this.minGroup = minGroup;
    this.interval = interval;
    this.colorSets = colorSets;
    this.fadeDuration = fadeDuration;
    this.resizeDelay = resizeDelay;
    this.imageChance = imageChance;
    this.maxTilePx = Math.max(minGroup * 2, maxTilePx); // 安全値: 2×minGroup以上

    this.ctx = canvas.getContext("2d");
    this.colorIndex = 0;
    this._intervalId = null;
    this._debounceId = null;
    this._isResizing = false;

    this._imgPool = [];
    this._allImagesLoaded = false;
    this.#preloadImages(images);

    this.#setCanvasSize();
    // 初期状態でフェードインクラスを付与
    this.canvas.classList.add("fade-in");

    this._boundResize = this.#handleResize.bind(this);
    window.addEventListener("resize", this._boundResize, { passive: true });
  }

  /** パターン描画を開始 */
  start() {
    if (this._allImagesLoaded) {
      this.paintPattern();
      this.#startInterval();
    } else {
      // 画像読み込み完了まで待機
      const waitId = setInterval(() => {
        if (this._allImagesLoaded) {
          clearInterval(waitId);
          this.paintPattern();
          this.#startInterval();
        }
      }, 100);
    }
  }

  /** パターン描画を停止 */
  stop() {
    clearInterval(this._intervalId);
    this._intervalId = null;
  }

  /** リソースを解放してイベントリスナを解除 */
  destroy() {
    this.stop();
    window.removeEventListener("resize", this._boundResize);
    clearTimeout(this._debounceId);
  }

  /** @private 自動更新用のインターバル開始 */
  #startInterval() {
    if (this._intervalId !== null) return;
    this._intervalId = setInterval(() => this.#cycle(), this.interval);
  }

  /** @private 次のパターンサイクルを実行 */
  #cycle() {
    if (this._isResizing) return;
    this.#fadeOutIn(() => {
      this.colorIndex = (this.colorIndex + 1) % this.colorSets.length;
      this.paintPattern();
    });
  }

  /** @private
   * フェードアウト→コールバック→フェードインのアニメーション実行
   */
  #fadeOutIn(cb) {
    const [rectCol] = this.colorSets[this.colorIndex];
    this.canvas.parentElement.style.backgroundColor = rectCol;

    // fade-in → fade-out
    this.canvas.classList.replace("fade-in", "fade-out");

    setTimeout(() => {
      cb();
      // fade-out → fade-in
      this.canvas.classList.replace("fade-out", "fade-in");
      // ★ fade-in が消えてしまうケースに備えて、必ず付与
      this.canvas.classList.add("fade-in");
      // 次回の備え：fade-out をクリア
      setTimeout(
        () => this.canvas.classList.remove("fade-out"),
        this.fadeDuration
      );
    }, this.fadeDuration);
  }

  /** @private リサイズ時のハンドラ（デバウンス処理付き） */
  #handleResize() {
    if (!this._isResizing) {
      this._isResizing = true;
      this.canvas.classList.replace("fade-in", "fade-out");
      this.stop();
    }
    clearTimeout(this._debounceId);
    this._debounceId = setTimeout(() => {
      this.#setCanvasSize();
      this.paintPattern();
      // fade-out → fade-in
      this.canvas.classList.replace("fade-out", "fade-in");
      // ★ 確実に fade-in を残す
      this.canvas.classList.add("fade-in");
      // fade-out はクリア
      setTimeout(
        () => this.canvas.classList.remove("fade-out"),
        this.fadeDuration
      );

      setTimeout(() => {
        this._isResizing = false;
        this.#startInterval();
      }, this.fadeDuration);
    }, this.resizeDelay);
  }

  /** パターンを画面全体に描画 */
  paintPattern() {
    const [rectCol, circCol] = this.colorSets[this.colorIndex];
    const cols = Math.ceil(window.innerWidth / this.minGroup);
    const rows = Math.ceil(window.innerHeight / this.minGroup);
    const used = Array.from({ length: rows }, () => Array(cols).fill(false));

    // キャンバスをクリア
    this.ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

    // タイルサイズの最大べき乗を計算
    const windowPow = Math.floor(Math.log2(Math.max(cols, rows)));
    const maxPxPow = Math.floor(Math.log2(this.maxTilePx / this.minGroup));
    const maxPow = Math.max(0, Math.min(windowPow, maxPxPow));

    // 大きいタイルから順にパターン配置
    for (let pow = maxPow; pow >= 0; pow--) {
      const cellSize = 2 ** pow;
      const grpPx = cellSize * this.minGroup;

      for (let gy = 0; gy <= rows - cellSize; gy += cellSize) {
        for (let gx = 0; gx <= cols - cellSize; gx += cellSize) {
          if (this.#cellUsed(used, gx, gy, cellSize)) continue;

          // pow=0 か確率 0.55 でタイルを確定
          if (pow === 0 || Math.random() < 0.55) {
            const px = gx * this.minGroup;
            const py = gy * this.minGroup;

            // 画像を使うか判定（端ほど配置率UP）
            let usePhoto = false;
            if (this._imgPool.length) {
              const cx = px + grpPx / 2;
              const distX =
                Math.abs(cx - window.innerWidth / 2) / (window.innerWidth / 2);
              const chance = this.imageChance * distX;
              usePhoto = Math.random() < chance;
            }

            if (usePhoto) {
              this.#drawPhotoTile(px, py, grpPx, rectCol);
            } else {
              const flip = (gx / cellSize + gy / cellSize) % 2 === 0;
              const [rC, cC] = flip ? [rectCol, circCol] : [circCol, rectCol];
              this.#drawGroup(px, py, grpPx, rC, cC);
            }
            this.#markUsed(used, gx, gy, cellSize);
          }
        }
      }
    }
  }

  /** @private 写真タイルを描画（半透明オーバーレイ） */
  #drawPhotoTile(x, y, size, rectC) {
    const img = this._imgPool[Math.floor(Math.random() * this._imgPool.length)];
    if (!img.complete) return;
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - side) / 2;
    const sy = (img.naturalHeight - side) / 2;

    this.ctx.save();
    // 背景矩形
    this.ctx.fillStyle = rectC;
    this.ctx.fillRect(x, y, size, size);
    // 半透明で写真を重ねる
    this.ctx.globalAlpha = 0.25;
    this.ctx.drawImage(img, sx, sy, side, side, x, y, size, size);
    this.ctx.restore();
  }

  /** @private 単一タイル（矩形＋円）を描画 */
  #drawTile(x, y, s, rectC, circC) {
    this.ctx.fillStyle = rectC;
    this.ctx.fillRect(x, y, s, s);
    this.ctx.fillStyle = circC;
    this.ctx.beginPath();
    this.ctx.arc(x + s / 2, y + s / 2, s / 2, 0, Math.PI * 2);
    this.ctx.fill();
  }

  /** @private 2×2 タイルグループを描画 */
  #drawGroup(x, y, g, rectC, circC) {
    const t = g / 2;
    this.#drawTile(x, y, t, rectC, circC);
    this.#drawTile(x + t, y, t, circC, rectC);
    this.#drawTile(x, y + t, t, circC, rectC);
    this.#drawTile(x + t, y + t, t, rectC, circC);
  }

  /** @private 画像リストをプリロードしプールに格納 */
  #preloadImages(srcList) {
    if (!srcList.length) {
      this._allImagesLoaded = true;
      return;
    }
    this._imgPool = [];
    let loaded = 0,
      total = srcList.length;
    const checkDone = () => {
      if (++loaded >= total) this._allImagesLoaded = true;
    };

    srcList.forEach((src) => {
      const img = src instanceof HTMLImageElement ? src : new Image();
      img.crossOrigin = "anonymous";
      const onFinish = () => {
        if (img.naturalWidth && img.naturalHeight) this._imgPool.push(img);
        else console.warn(`画像読み込み失敗: ${src}`);
        checkDone();
      };
      img.onload = onFinish;
      img.onerror = onFinish;
      if (!(src instanceof HTMLImageElement)) img.src = src;
      else if (src.complete) onFinish();
    });
  }

  /** @private セルが既に使用済みかチェック */
  #cellUsed(mat, gx, gy, size) {
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) if (mat[gy + y][gx + x]) return true;
    return false;
  }

  /** @private セルを使用済みにマーク */
  #markUsed(mat, gx, gy, size) {
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) mat[gy + y][gx + x] = true;
  }

  /** @private canvas 要素をウィンドウサイズに合わせて更新 */
  #setCanvasSize() {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = window.innerWidth * dpr;
    this.canvas.height = window.innerHeight * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** デフォルトのカラーセット */
  static get defaultColors() {
    return [
      ["#ffffff", "#000000"],
      ["#000000", "#ffffff"],
    ];
  }
}
